import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DataGrid from '../../UI/DataGrid/DataGrid';
import { getAllCapteurByProjectId } from 'services/api';

interface SensorItem {
  sensor_id: number;
  sensor_name: string;
  view_type?: string;
  remarks?: string;
}

interface SensorsListInputProps {
  argument: any;
  onChange: (field: string, value: any) => void;
  selectionMode?: 'single' | 'multiple';
  showInfo?: boolean;
  projectId?: string | number;
}

const SensorsListInput: React.FC<SensorsListInputProps> = ({
  argument,
  onChange,
  selectionMode = 'multiple',
  showInfo = true,
  projectId,
}) => {
  const projectIds = useSelector(
    (state: any) => state.authentifie.filtre_projects_list_id
  );
  const [availableSensors, setAvailableSensors] = useState<SensorItem[]>([]);
  const [selectedSensors, setSelectedSensors] = useState<SensorItem[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getSensors = async () => {
      setLoading(true);
      const response = await getAllCapteurByProjectId({
        projects_id: projectId ? [projectId] : projectIds,
      });
      setAvailableSensors(response.data.sensors_list || []);
      setSelectedSensors([]);
      setLoading(false);
    };
    getSensors();
  }, [projectId]);

  useEffect(() => {
    if (argument.valeurParDefaut || argument.value) {
      const value = argument.valeurParDefaut || argument.value;
      const isDevuiList = argument.type === 'DevuiList';

      let identifiers: (number | string)[] = [];

      try {
        if (typeof value === 'string') {
          if (value.startsWith('[')) {
            const parsed = JSON.parse(value);
            if (isDevuiList) {
              identifiers = parsed.map((id: any) => String(id));
            } else {
              identifiers = parsed.map((id: any) => parseInt(id, 10));
            }
          } else {
            if (isDevuiList) {
              identifiers = value
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id !== '');
            } else {
              identifiers = value
                .split(',')
                .map((id) => parseInt(id.trim(), 10))
                .filter((id) => !isNaN(id));
            }
          }
        } else if (Array.isArray(value)) {
          identifiers = isDevuiList
            ? value.map((id) => String(id))
            : value.map((id) => parseInt(id, 10));
        }
      } catch (error) {
        console.error('Error parsing sensor identifiers:', error);
      }

      const selected = availableSensors.filter((sensor) => {
        if (isDevuiList) {
          return identifiers.includes(sensor.remarks || '');
        } else {
          return identifiers.includes(sensor.sensor_id);
        }
      });
      setSelectedSensors(selected);
    }
  }, [
    argument.valeurParDefaut,
    argument.value,
    argument.type,
    availableSensors,
  ]);

  const handleSelectionChange = (sensors: SensorItem[]) => {
    setSelectedSensors(sensors);

    const isDevuiList = argument.type === 'DevuiList';

    const identifiers = isDevuiList
      ? sensors.map((s) => s.remarks || '')
      : sensors.map((s) => s.sensor_id);

    const formattedValue = JSON.stringify(identifiers);

    onChange('valeurParDefaut', formattedValue);
  };

  const columns = [
    {
      field: 'sensor_id',
      headerName: 'ID du capteur',
      flex: 0.5,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'sensor_name',
      headerName: 'Nom du capteur',
      flex: 1,
      minWidth: 220,
      sortable: true,
    },
  ];

  const rowSelectionModel = selectedSensors.map((sensor) => sensor.sensor_id);

  const handleRowSelectionModelChange = (ids: any[]) => {
    if (selectionMode === 'single') {
      const selectedId = ids.length > 0 ? ids[ids.length - 1] : null;
      const sensor = availableSensors.find(
        (item) => item.sensor_id === selectedId
      );
      handleSelectionChange(sensor ? [sensor] : []);
      return;
    }

    const selectedIds = new Set(ids);
    const sensors = availableSensors.filter((sensor) =>
      selectedIds.has(sensor.sensor_id)
    );
    handleSelectionChange(sensors);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          border: '1px solid #dee2e6',
        }}
      >
        <div>
          <h6 style={{ margin: 0, color: '#495057' }}>
            Sélection des capteurs{' '}
            {selectionMode === 'single' && '(sélection unique)'}
          </h6>
          {selectedSensors.length > 0 && (
            <small style={{ color: '#6c757d' }}>
              {selectedSensors.length} capteur(s) sélectionné(s)
            </small>
          )}
        </div>
        {showInfo && (
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              style={{
                backgroundColor: 'rgb(60, 57, 53)',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
              }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
            >
              i
            </button>
            {showTooltip && (
              <div
                style={{
                  position: 'absolute',
                  top: '35px',
                  right: '0',
                  backgroundColor: '#343a40',
                  color: 'white',
                  padding: '12px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  zIndex: 1000,
                  width: '300px',
                  fontSize: '13px',
                  lineHeight: '1.4',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  ℹ️ Information sur la sélection
                </div>
                <div style={{ marginBottom: '6px' }}>
                  • <strong>Mode:</strong>{' '}
                  {selectionMode === 'single'
                    ? 'Sélection unique'
                    : 'Sélection multiple'}
                </div>
                <div style={{ color: '#ffc107' }}>
                  💡{' '}
                  {selectionMode === 'single'
                    ? "Vous ne pouvez sélectionner qu'un seul capteur."
                    : 'Vous pouvez sélectionner plusieurs capteurs.'}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '10px',
                    width: '0',
                    height: '0',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderBottom: '6px solid #343a40',
                  }}
                ></div>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#6c757d',
          }}
        >
          Chargement des capteurs...
        </div>
      ) : availableSensors.length === 0 ? (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#6c757d',
            border: '1px solid #dee2e6',
            borderRadius: '5px',
          }}
        >
          Aucun capteur disponible pour ce projet.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            borderRadius: '5px',
          }}
        >
          <DataGrid
            rows={availableSensors}
            columns={columns}
            getRowId={(row: SensorItem) => row.sensor_id}
            checkboxSelection={selectionMode === 'multiple'}
            disableMultipleRowSelection={selectionMode === 'single'}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            rowSelectionModel={rowSelectionModel as never[]}
            pageSizeOptions={[10, 25, 50]}
            initialPageSize={10}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default SensorsListInput;
